import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';

import Logo from '../assets/logo.png';


const gymBackend = (process.env.REACT_APP_GYM_BACKEND_URL || 'http://127.0.0.1:8002').replace(/\/$/, '');


const getResponseMessage = (data, fallback) => {
    if (typeof data?.detail === 'string') return data.detail;
    if (typeof data?.message === 'string') return data.message;

    const firstValue = data && typeof data === 'object' ? Object.values(data)[0] : null;
    if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    return fallback;
};


const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState({ status: '', message: '' });

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!token) {
            setResult({ status: 'error', message: 'Le lien de réinitialisation est incomplet.' });
            return;
        }
        if (newPassword !== confirmPassword) {
            setResult({ status: 'error', message: 'Les deux mots de passe ne correspondent pas.' });
            return;
        }

        setLoading(true);
        setResult({ status: '', message: '' });

        try {
            const response = await fetch(`${gymBackend}/verification/reset-password/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    new_password: newPassword,
                    confirm_password: confirmPassword,
                }),
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                setResult({
                    status: 'error',
                    message: getResponseMessage(data, 'Le mot de passe n’a pas pu être modifié.'),
                });
                return;
            }

            setResult({
                status: 'success',
                message: getResponseMessage(data, 'Votre mot de passe a été modifié.'),
            });
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            setResult({
                status: 'error',
                message: 'Le serveur est inaccessible. Veuillez réessayer.',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'grid',
                placeItems: 'center',
                bgcolor: '#f4f6fb',
                p: 2,
            }}
        >
            <Card sx={{ width: '100%', maxWidth: 500, borderRadius: 4, boxShadow: '0 20px 60px rgba(1,4,73,.12)' }}>
                <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                    <Box textAlign="center" sx={{ mb: 3 }}>
                        <img src={Logo} alt="Gyma" style={{ width: 72, height: 'auto' }} />
                        <Typography variant="h4" fontWeight={800} color="#010449" sx={{ mt: 2 }}>
                            Nouveau mot de passe
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                            Choisissez un mot de passe sécurisé pour votre espace salle.
                        </Typography>
                    </Box>

                    {result.message && (
                        <Alert severity={result.status === 'success' ? 'success' : 'error'} sx={{ mb: 3 }}>
                            {result.message}
                        </Alert>
                    )}

                    {result.status !== 'success' ? (
                        <Box component="form" onSubmit={handleSubmit}>
                            <Stack spacing={2}>
                                <TextField
                                    label="Nouveau mot de passe"
                                    type="password"
                                    value={newPassword}
                                    onChange={(event) => setNewPassword(event.target.value)}
                                    required
                                    fullWidth
                                    autoComplete="new-password"
                                    disabled={loading || !token}
                                    helperText="Utilisez au moins 8 caractères et évitez les mots de passe trop courants."
                                />
                                <TextField
                                    label="Confirmer le nouveau mot de passe"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(event) => setConfirmPassword(event.target.value)}
                                    required
                                    fullWidth
                                    autoComplete="new-password"
                                    disabled={loading || !token}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={loading || !token}
                                    sx={{ bgcolor: '#010449', py: 1.35, borderRadius: 2.5 }}
                                >
                                    {loading
                                        ? <CircularProgress size={24} color="inherit" />
                                        : 'Modifier mon mot de passe'}
                                </Button>
                            </Stack>
                        </Box>
                    ) : (
                        <Button
                            component={Link}
                            to="/auth"
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{ bgcolor: '#010449', py: 1.35, borderRadius: 2.5 }}
                        >
                            Se connecter
                        </Button>
                    )}

                    {!token && (
                        <Button component={Link} to="/auth" fullWidth sx={{ mt: 2 }}>
                            Demander un nouveau lien
                        </Button>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default ResetPassword;
